import { useAppDispatch } from 'client/hooks/use-dispatch'
import { useWebsiteSettings } from 'client/store'
import { localeNames } from 'common/constants/localeTypes'
import { PageLocaleEnum } from 'common/enums/PageLocaleEnum'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutationWithValidation } from 'src/editor/api/hooks/use-api-mutation-with-validation'
import { websitePagesQuery } from 'src/editor/api/queries/website-pages-query'
import {
  addWebsiteLocale,
  copyWebsitePages,
} from 'src/editor/api/website-websites-api'
import { ConfirmationDialogWindow } from 'src/editor/shared/ui-lib/modal-window/dialog-window/confirmation-dialog-window'
import {
  addWebsiteLayout,
  setWebsiteLocales,
} from 'src/editor/store/website-settings/website-settings-actions'
import {
  getWebsiteId,
  getWebsiteLocales,
} from 'src/editor/store/website-settings/website-settings-selector'

interface AddWebsiteLocaleProps {
  localeToAdd: PageLocaleEnum | null
  onFinallyAddLocale: () => void
}

export const AddWebsiteLocale = ({
  localeToAdd,
  onFinallyAddLocale,
}: AddWebsiteLocaleProps) => {
  const { t } = useTranslation()
  const [copyPagesLocale, setCopyPagesLocale] = useState<PageLocaleEnum | null>(
    null,
  )
  const dispatch = useAppDispatch()

  const websiteId = useWebsiteSettings(getWebsiteId) as number
  const websiteLocales = useWebsiteSettings(getWebsiteLocales) || []
  const localesWithAddedLocale = localeToAdd
    ? [...websiteLocales, localeToAdd]
    : websiteLocales

  const handleCloseCopyPages = () => {
    setCopyPagesLocale(null)
  }

  const { fetcher: fetcherAddLocale } = useMutationWithValidation({
    requestFunction: addWebsiteLocale,
    successToastMessage: t('website_settings.add_locale.added_successfully'),
    handleSuccess: response => {
      dispatch(setWebsiteLocales(localesWithAddedLocale))
      if (localeToAdd) {
        dispatch(
          addWebsiteLayout({
            locale: localeToAdd,
            layoutPageId: response.layoutPageContentId,
          }),
        )
      }

      setCopyPagesLocale(localeToAdd)
    },
    handleFinally: () => {
      onFinallyAddLocale()
    },
    invalidateKeyOnSuccess: websitePagesQuery.getQueryKey(websiteId),
  })

  const { fetcher: fetcherCopyPages, isLoading: isLoadingCopyPages } =
    useMutationWithValidation({
      requestFunction: copyWebsitePages,
      successToastMessage: t('website_settings.copy_pages.copied_successfully'),
      handleSuccess: () => {
        setCopyPagesLocale(null)
      },
      invalidateKeyOnSuccess: websitePagesQuery.getQueryKey(websiteId),
    })

  useEffect(() => {
    if (localeToAdd) {
      fetcherAddLocale({
        id: websiteId,
        locale: localeToAdd,
      })
    }
  }, [localeToAdd])

  const handleCopyPages = () => {
    fetcherCopyPages({
      id: websiteId,
      locale: copyPagesLocale as PageLocaleEnum,
    })
  }

  return (
    <ConfirmationDialogWindow
      isOpened={!!copyPagesLocale}
      onClose={handleCloseCopyPages}
      title={t('website_settings.copy_pages.confirmation_window.title_icu', {
        locale: copyPagesLocale ? t(localeNames[copyPagesLocale]) : '',
      })}
      description={t(
        'website_settings.copy_pages.confirmation_window.description',
      )}
      cancelButtonProps={{
        children: t('global.skip'),
        onClick: handleCloseCopyPages,
      }}
      confirmButtonProps={{
        isLoading: isLoadingCopyPages,
        onClick: handleCopyPages,
        children: t(
          'website_settings.copy_pages.confirmation_window.copy_button',
        ),
      }}
    />
  )
}
